/**
 * GrowthLineChart renders a line chart (cumulative or per-period) with Meridian
 * LegendProvider integration and a TrendText showing % change from the reference
 * Meridian dashboard pattern.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import TrendText from '@amzn/meridian/trend-text';
import {
  Legend,
  LegendProvider,
  useVictoryTheme,
  victoryLinePlugin,
  victoryTooltipPlugin,
} from '@amzn/meridian';
import {
  VictoryChart,
  VictoryVoronoiContainer,
  VictoryAxis,
  VictoryLine,
} from 'victory';
import { PUBLISHER_CHART_COLORS } from 'src/constants';
import { useTranslation } from 'src/arb-wrapper';

const FALLBACK_COLOR = '#6B7280';

interface DataPoint {
  label?: string;
  date?: string;
  value: number;
}

interface GrowthLineChartProps {
  data: DataPoint[];
  title: string;
  isLoading?: boolean;
  /** If true, shows cumulative (running total). If false, shows per-period counts. Default: true */
  cumulative?: boolean;
}

/** Calculate the average % change between the last two data points across all series */
const calcTrendPercent = (
  dataset: Array<{ data: Array<{ value: number }> }>,
): number => {
  const percentages = dataset.map((series) => {
    const pts = series.data;
    if (pts.length < 2) return 0;
    const prev = pts[pts.length - 2].value;
    const curr = pts[pts.length - 1].value;
    if (prev === 0) return 0;
    return Math.floor(((curr - prev) / prev) * 100);
  });
  const sum = percentages.reduce((a, b) => a + b, 0);
  return percentages.length > 0 ? Math.floor(sum / percentages.length) : 0;
};

export const GrowthLineChart: React.FC<GrowthLineChartProps> = ({
  data,
  title,
  isLoading,
  cumulative = true,
}) => {
  const { t } = useTranslation();
  const observerRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(800);
  const theme = useVictoryTheme({ showIndependentGrid: false });

  const ref = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (el) {
      observerRef.current = new ResizeObserver((entries) => {
        setWidth(entries[0].contentRect.width);
      });
      observerRef.current.observe(el);
    }
  }, []);

  const dataset = useMemo(() => {
    const allDates = Array.from(new Set(data.map((d) => d.date ?? '')))
      .filter(Boolean)
      .sort();
    const labels = Array.from(
      new Set(data.map((d) => d.label).filter(Boolean)),
    ) as string[];

    return labels.map((label) => {
      const countByDate = new Map(
        data
          .filter((d) => d.label === label)
          .map((d) => [d.date ?? '', d.value]),
      );
      let running = 0;
      return {
        key: label,
        label,
        color: PUBLISHER_CHART_COLORS[label] ?? FALLBACK_COLOR,
        data: allDates.map((date) => {
          const periodValue = countByDate.get(date) ?? 0;
          running += periodValue;
          return { time: date, value: cumulative ? running : periodValue };
        }),
      };
    });
  }, [data, cumulative]);

  const trendPercent = useMemo(() => calcTrendPercent(dataset), [dataset]);
  const hasData = dataset.some((ds) => ds.data.length > 0);

  return (
    <Column
      backgroundColor="white"
      heights={['fit', 'fill']}
      spacing="none"
      spacingInset="400"
      type="outline"
    >
      <Heading level={4}>{title}</Heading>

      {isLoading
        ? (
        <Column
          alignmentHorizontal="center"
          alignmentVertical="center"
          minHeight={280}
        >
          <Loader />
        </Column>
          )
        : !hasData
            ? (
        <Column
          alignmentHorizontal="center"
          alignmentVertical="center"
          minHeight={200}
        >
          <Text color="secondary">{t('chart_no_data_available')}</Text>
        </Column>
              )
            : (
        <LegendProvider
          aria-label={title}
          data={dataset}
          plugins={[
            victoryLinePlugin({ theme }),
            victoryTooltipPlugin({ showCrosshair: true }),
          ]}
        >
          {({
            getLineProps,
            voronoiContainerProps,
            Tooltip,
          }: {
            getLineProps: (key: string) => Record<string, unknown>;
            voronoiContainerProps: Record<string, unknown>;
            Tooltip: React.ComponentType<{ legendKey: string }>;
          }) => (
            <Column spacing="none" width="100%">
              {trendPercent !== 0 && (
                <TrendText
                  arrow={trendPercent > 0 ? 'up' : 'down'}
                  color={trendPercent > 0 ? 'positive' : 'negative'}
                  size="medium"
                >
                  {t('chart_percent_this_period', { percent: Math.abs(trendPercent) })}
                </TrendText>
              )}

              <div ref={ref} style={{ width: '100%' }}>
                <VictoryChart
                  containerComponent={
                    <VictoryVoronoiContainer
                      labels={({ datum }) => `${datum.time}: ${datum.value}`}
                      {...voronoiContainerProps}
                    />
                  }
                  domainPadding={{ x: 16 }}
                  height={300}
                  minDomain={{ y: 0 }}
                  padding={{ top: 20, bottom: 60, left: 60, right: 20 }}
                  theme={theme}
                  width={width}
                >
                  <VictoryAxis
                    style={{
                      tickLabels: {
                        fontSize: 10,
                        angle: -45,
                        textAnchor: 'end',
                      },
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    style={{ tickLabels: { fontSize: 10 } }}
                  />
                  {dataset.map(({ data: lineData, key }) => (
                    <VictoryLine
                      data={lineData}
                      key={key}
                      labelComponent={<Tooltip legendKey={key} />}
                      labels={({ datum }) => datum.value}
                      x="time"
                      y="value"
                      {...getLineProps(key)}
                    />
                  ))}
                </VictoryChart>
              </div>

              <Column spacingInset="none none 400 400">
                <Legend direction="horizontal" />
              </Column>
            </Column>
          )}
        </LegendProvider>
              )}
    </Column>
  );
};
